// ============================================================================
// HOOPS FOR THOUGHT: CBA TRADE ENGINE
// 2025-26 Season Rules (New CBA)
// ============================================================================

// --- CBA THRESHOLDS ---
export const CBA = {
  salaryCap: 154647000,
  luxuryTax: 187895000,
  firstApron: 195945000,
  secondApron: 207824000,
  salaryFloor: 139182000,
  // Expanded TPE flat amount for teams under both aprons
  expandedTPE: 8527000,
  // Min salary for matching (2 YOS)
  minSalary: 2296274,
};

// --- HARD CAP DATA (2025-26 Season) ---
// Source: Hoops Rumors, updated through Feb 2026
// Teams hard-capped at 1st apron due to offseason moves
const FIRST_APRON_HARD_CAPPED = [
  'ATL', // S&T for Alexander-Walker, TPE for Alexander-Walker
  'CHI', // Expanded TPE for Sexton, TPE for Okoro
  'DET', // S&T for Duncan Robinson
  'HOU', // S&T for Capela, expanded TPE for Capela/Durant
  'IND', // TPE for Jay Huff
  'LAC', // Non-taxpayer MLE for Brook Lopez
  'LAL', // Non-taxpayer MLE for Jake LaRavia
  'MEM', // Expanded TPE for KCP and Cole Anthony
  'NOP', // Non-taxpayer MLE for Kevon Looney
  'ORL', // S&T for Desmond Bane
  'SAC', // TPE for Zach LaVine
  'TOR', // Non-taxpayer MLE for Trayce Jackson-Davis (via trade)
  'UTA', // Non-taxpayer MLE for Kyle Anderson, BAE for Kevin Love, Expanded TPE for JJJ
];

// Teams hard-capped at 2nd apron due to using taxpayer MLE
const SECOND_APRON_HARD_CAPPED = [
  'GSW', // Taxpayer MLE for Al Horford
  'WAS', // Taxpayer MLE for D'Angelo Russell
];

// --- TEAM STATUS HELPERS ---

/**
 * Get a team's salary tier based on current payroll
 */
export function getTeamTier(payroll) {
  if (payroll <= CBA.salaryCap) return 'under_cap';
  if (payroll <= CBA.firstApron) return 'over_cap';
  if (payroll <= CBA.secondApron) return 'first_apron';
  return 'second_apron';
}

/**
 * Get a team's hard cap (if any)
 */
export function getHardCap(teamAbbr) {
  if (FIRST_APRON_HARD_CAPPED.includes(teamAbbr)) return CBA.firstApron;
  if (SECOND_APRON_HARD_CAPPED.includes(teamAbbr)) return CBA.secondApron;
  return null;
}

/**
 * Get human-readable tier label
 */
export function getTierLabel(tier) {
  switch (tier) {
    case 'under_cap': return 'Under Cap';
    case 'over_cap': return 'Over Cap (Expanded Matching)';
    case 'first_apron': return 'Above 1st Apron';
    case 'second_apron': return 'Above 2nd Apron';
    default: return tier;
  }
}

// --- SALARY MATCHING ---

/**
 * Calculate maximum incoming salary a team can receive for given outgoing salary
 * Based on team's salary tier
 */
export function getMaxIncoming(outgoingSalary, tier) {
  if (outgoingSalary <= 0) return 0;

  switch (tier) {
    case 'under_cap':
      // Handled separately via cap space absorption
      // But if they need matching (going over cap), use expanded rules
      return getMaxIncoming(outgoingSalary, 'over_cap');

    case 'over_cap':
      // Expanded matching (under both aprons)
      // Three brackets:
      if (outgoingSalary <= 7500000) {
        return Math.floor(outgoingSalary * 2 + 250000);
      }
      // Breakpoint where 125% + $250K > outgoing + $8,527,000:
      // 1.25x + 250000 = x + 8527000 → x = $33,108,000
      if (outgoingSalary <= 33108000) {
        return outgoingSalary + CBA.expandedTPE;
      }
      return Math.floor(outgoingSalary * 1.25 + 250000);

    case 'first_apron':
      // Above 1st apron: 110% only (no +$100K padding under new CBA)
      return Math.floor(outgoingSalary * 1.10);

    case 'second_apron':
      // Above 2nd apron: cannot take back more than sent out
      return outgoingSalary;

    default:
      return outgoingSalary;
  }
}

/**
 * Core trade validation
 * 
 * @param {Object} tradeData - {
 *   teams: { [abbr]: { payroll, hardCap, players: [...allPlayers] } },
 *   packages: { [abbr]: [...playersBeingSent] },
 *   phase: 'regular' | 'postDeadline' | 'offseason'
 * }
 * @returns {{ valid: boolean, errors: string[], warnings: string[], breakdown: Object }}
 */
export function validateTrade(tradeData) {
  const { teams, packages, phase = 'regular' } = tradeData;
  const teamAbbrs = Object.keys(packages);
  const errors = [];
  const warnings = [];
  const breakdown = {};

  // === POST-DEADLINE: No trades allowed ===
  if (phase === 'postDeadline') {
    return {
      valid: false,
      errors: ['The trade deadline has passed. Trades cannot be made until the offseason.'],
      warnings,
      breakdown,
    };
  }

  // Must have at least 2 teams
  if (teamAbbrs.length < 2) {
    return { valid: false, errors: ['Need at least 2 teams in a trade'], warnings, breakdown };
  }

  // Each team must be sending at least one player
  for (const abbr of teamAbbrs) {
    if (!packages[abbr] || packages[abbr].length === 0) {
      errors.push(`${abbr} must send at least one player`);
    }
  }
  if (errors.length > 0) {
    return { valid: false, errors, warnings, breakdown };
  }

  // Calculate what each team sends and receives
  for (const abbr of teamAbbrs) {
    const team = teams[abbr];
    const sending = packages[abbr];
    const receiving = [];

    // Collect all players this team receives (sent by other teams)
    for (const otherAbbr of teamAbbrs) {
      if (otherAbbr !== abbr) {
        receiving.push(...packages[otherAbbr].map(p => ({ ...p, from: otherAbbr })));
      }
    }

    const outgoingTotal = sending.reduce((sum, p) => sum + (p.salary2526 || 0), 0);
    const incomingTotal = receiving.reduce((sum, p) => sum + (p.salary2526 || 0), 0);
    const currentPayroll = team.payroll;
    const postTradePayroll = currentPayroll - outgoingTotal + incomingTotal;
    const tier = getTeamTier(currentPayroll);
    const hardCap = team.hardCap;

    breakdown[abbr] = {
      sending,
      receiving,
      outgoingTotal,
      incomingTotal,
      currentPayroll,
      postTradePayroll,
      tier,
      hardCap,
      netChange: incomingTotal - outgoingTotal,
    };

    // === CHECK 1: Hard cap ===
    // In the offseason, prior-season hard caps reset.
    // New hard caps only apply from new offseason transactions (S&T, MLE usage).
    if (hardCap && phase !== 'offseason' && postTradePayroll > hardCap) {
      const capLabel = hardCap === CBA.firstApron ? '1st Apron' : '2nd Apron';
      errors.push(
        `${abbr} is hard-capped at the ${capLabel} ($${(hardCap / 1000000).toFixed(1)}M). ` +
        `Post-trade payroll of $${(postTradePayroll / 1000000).toFixed(1)}M exceeds this.`
      );
    }

    // === CHECK 2: Salary matching based on tier ===
    if (tier === 'under_cap') {
      // Team has cap space — can absorb into available room
      const capSpace = CBA.salaryCap - currentPayroll;
      if (incomingTotal <= outgoingTotal + capSpace) {
        // Trade works — absorbing via cap space
        breakdown[abbr].matchMethod = 'Cap Space Absorption';
        breakdown[abbr].maxIncoming = outgoingTotal + capSpace;
      } else {
        // Even with cap space, too much incoming
        // Actually, once they go over cap they use standard matching on the full amounts
        const maxIncoming = getMaxIncoming(outgoingTotal, 'over_cap');
        breakdown[abbr].matchMethod = 'Expanded Matching (over cap after trade)';
        breakdown[abbr].maxIncoming = Math.max(maxIncoming, outgoingTotal + capSpace);

        if (incomingTotal > breakdown[abbr].maxIncoming) {
          errors.push(
            `${abbr} cap space + matching allows max $${(breakdown[abbr].maxIncoming / 1000000).toFixed(1)}M incoming. ` +
            `Receiving $${(incomingTotal / 1000000).toFixed(1)}M exceeds this.`
          );
        }
      }
    } else if (tier === 'second_apron') {
      // === SECOND APRON: No aggregation ===
      breakdown[abbr].matchMethod = '2nd Apron (No Aggregation)';

      // Each outgoing player must individually match an incoming player
      // Cannot combine multiple outgoing salaries to absorb one bigger contract
      const sortedOut = [...sending].sort((a, b) => b.salary2526 - a.salary2526);
      const sortedIn = [...receiving].sort((a, b) => b.salary2526 - a.salary2526);

      if (sortedIn.length > sortedOut.length) {
        errors.push(
          `${abbr} is above the 2nd Apron — cannot receive more players (${sortedIn.length}) than sending (${sortedOut.length}) due to no-aggregation rule.`
        );
      } else {
        // Try to match each incoming player to an outgoing player
        const usedOut = new Set();
        for (const inPlayer of sortedIn) {
          let matched = false;
          for (let i = 0; i < sortedOut.length; i++) {
            if (usedOut.has(i)) continue;
            if (sortedOut[i].salary2526 >= inPlayer.salary2526) {
              usedOut.add(i);
              matched = true;
              break;
            }
          }
          if (!matched) {
            errors.push(
              `${abbr} is above the 2nd Apron — no aggregation allowed. ` +
              `Cannot match incoming ${inPlayer.name} ($${(inPlayer.salary2526 / 1000000).toFixed(1)}M) ` +
              `to any single outgoing player.`
            );
          }
        }
      }
      breakdown[abbr].maxIncoming = outgoingTotal; // total can't exceed outgoing
    } else {
      // Over cap or above 1st apron — standard matching
      const maxIncoming = getMaxIncoming(outgoingTotal, tier);
      const methodLabel = tier === 'first_apron' ? '1st Apron (110%)' : 'Expanded (Under Aprons)';
      breakdown[abbr].matchMethod = methodLabel;
      breakdown[abbr].maxIncoming = maxIncoming;

      if (incomingTotal > maxIncoming) {
        errors.push(
          `${abbr} sends $${(outgoingTotal / 1000000).toFixed(1)}M — ` +
          `max incoming is $${(maxIncoming / 1000000).toFixed(1)}M (${methodLabel}). ` +
          `Receiving $${(incomingTotal / 1000000).toFixed(1)}M exceeds this by $${((incomingTotal - maxIncoming) / 1000000).toFixed(1)}M.`
        );
      }
    }

    // === WARNINGS ===
    if (!errors.length) {
      const postTier = getTeamTier(postTradePayroll);
      if (postTier === 'second_apron' && tier !== 'second_apron') {
        warnings.push(`${abbr} would move ABOVE the 2nd Apron after this trade.`);
      } else if (postTier === 'first_apron' && tier !== 'first_apron' && tier !== 'second_apron') {
        warnings.push(`${abbr} would move above the 1st Apron after this trade.`);
      }
      if (postTradePayroll > CBA.luxuryTax && currentPayroll <= CBA.luxuryTax) {
        warnings.push(`${abbr} would enter the Luxury Tax after this trade.`);
      }

      // Offseason-specific note
      if (phase === 'offseason') {
        if (hardCap) {
          warnings.push(`${abbr} hard cap from last season has reset. New hard caps apply only from offseason transactions (S&T, MLE).`);
        }
      }
    }
  }

  // In a multi-team trade, all players sent must be received by someone
  // (This is inherent in our structure — packages define who leaves, and everyone
  //  not on the sending team receives from other teams)

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    breakdown,
  };
}

/**
 * Execute a trade — returns new salary data with players swapped
 * In a multi-team trade, each team receives all players sent by OTHER teams.
 * For 3+ team trades, you may want directional control (who goes where),
 * but for V1, sent players go into a pool and each team receives from the pool.
 */
export function executeTrade(salaryData, packages, destinations) {
  // Deep clone salary data
  const newData = JSON.parse(JSON.stringify(salaryData));

  // destinations[abbr] = { playerName: destinationAbbr }
  // If no destinations specified, default: all sent players go to "the other team"
  // For 2-team trades this is automatic. For 3-team, we need explicit destinations.

  const teamAbbrs = Object.keys(packages);

  // Collect all players being moved with their destination
  const moves = []; // { player, from, to }

  if (teamAbbrs.length === 2) {
    // Simple 2-team swap
    const [teamA, teamB] = teamAbbrs;
    for (const p of packages[teamA]) {
      moves.push({ player: p, from: teamA, to: teamB });
    }
    for (const p of packages[teamB]) {
      moves.push({ player: p, from: teamB, to: teamA });
    }
  } else if (destinations) {
    // Use explicit destinations for 3+ team trades
    for (const abbr of teamAbbrs) {
      for (const p of packages[abbr]) {
        const dest = destinations[abbr]?.[p.name];
        if (dest) {
          moves.push({ player: p, from: abbr, to: dest });
        }
      }
    }
  }

  // Execute moves
  for (const move of moves) {
    const fromTeam = newData[move.from];
    const toTeam = newData[move.to];
    if (!fromTeam || !toTeam) continue;

    // Remove player from source
    const idx = fromTeam.players.findIndex(p => p.name === move.player.name);
    if (idx !== -1) {
      const [player] = fromTeam.players.splice(idx, 1);
      // Add to destination
      toTeam.players.push(player);
      // Update payrolls
      fromTeam.payroll -= player.salary2526;
      toTeam.payroll += player.salary2526;
    }
  }

  return newData;
}
